import { type ReactNode } from 'react'
import { useAuthStore } from '@/stores/authStore'
import type { Role } from '@/types'

export interface RolePermissions {
  canSeeFinancials: boolean
  canSeeAdminTab: boolean
  canSeeCommissions: boolean
  canSeeBdrPanel: boolean
  canSeeCloserLeads: boolean
  canSeeRocketSearch: boolean
  canSeeConfig: boolean
  defaultTab: string
}

const ROLE_PERMISSIONS: Record<Role, RolePermissions> = {
	  admin: {
	    canSeeFinancials: true,
	    canSeeAdminTab: true,
	    canSeeCommissions: true,
    canSeeBdrPanel: true,
    canSeeCloserLeads: true,
    canSeeRocketSearch: true,
    canSeeConfig: true,
	    defaultTab: 'summary',
	  },
	  manager: {
	    canSeeFinancials: true,
	    canSeeAdminTab: false,
	    canSeeCommissions: true,
	    canSeeBdrPanel: true,
	    canSeeCloserLeads: true,
	    canSeeRocketSearch: true,
	    canSeeConfig: false,
	    defaultTab: 'summary',
	  },
	  bdr: {
    canSeeFinancials: false,
    canSeeAdminTab: false,
    canSeeCommissions: false,
    canSeeBdrPanel: true,
    canSeeCloserLeads: true,
    canSeeRocketSearch: true,
    canSeeConfig: false,
    defaultTab: 'bdr',
  },
  closer: {
    canSeeFinancials: true,
    canSeeAdminTab: false,
    canSeeCommissions: false,
    canSeeBdrPanel: true,
    canSeeCloserLeads: true,
    canSeeRocketSearch: true,
    canSeeConfig: false,
    defaultTab: 'summary',
  },
  financeiro: {
    canSeeFinancials: true,
    canSeeAdminTab: false,
    canSeeCommissions: true,
    canSeeBdrPanel: false,
    canSeeCloserLeads: false,
    canSeeRocketSearch: false,
    canSeeConfig: false,
    defaultTab: 'commissions',
  },
  pos_venda: {
    canSeeFinancials: false,
    canSeeAdminTab: false,
    canSeeCommissions: false,
    canSeeBdrPanel: false,
    canSeeCloserLeads: false,
    canSeeRocketSearch: false,
    canSeeConfig: false,
    defaultTab: 'retention',
  },
  cs: {
    canSeeFinancials: false,
    canSeeAdminTab: false,
    canSeeCommissions: false,
    canSeeBdrPanel: false,
    canSeeCloserLeads: false,
    canSeeRocketSearch: false,
    canSeeConfig: false,
    defaultTab: 'retention',
  },
  usuario_agrada: {
    canSeeFinancials: false,
    canSeeAdminTab: false,
    canSeeCommissions: false,
    canSeeBdrPanel: false,
    canSeeCloserLeads: false,
    canSeeRocketSearch: false,
    canSeeConfig: false,
    defaultTab: 'summary',
  },
  indicador: {
    canSeeFinancials: false,
    canSeeAdminTab: false,
    canSeeCommissions: false,
    canSeeBdrPanel: false,
    canSeeCloserLeads: false,
    canSeeRocketSearch: false,
    canSeeConfig: false,
    defaultTab: 'summary',
  },
}

export function useRolePermissions(): RolePermissions {
  const { user } = useAuthStore()
  const role = (user?.role?.toLowerCase() ?? 'bdr') as Role
  return ROLE_PERMISSIONS[role] ?? ROLE_PERMISSIONS.bdr
}

interface RoleGuardProps {
  allowedRoles: Role[]
  children: ReactNode
  fallback?: ReactNode
}

export function RoleGuard({ allowedRoles, children, fallback = null }: RoleGuardProps) {
  const { user } = useAuthStore()
  const normalizedAllowed = allowedRoles.map(r => r.toLowerCase())
  const hasAccess = user && normalizedAllowed.includes(user.role.toLowerCase())

  if (!hasAccess) return fallback
  return <>{children}</>
}

interface PermissionGuardProps {
  permission: keyof RolePermissions
  children: ReactNode
  fallback?: ReactNode
}

export function PermissionGuard({ permission, children, fallback = null }: PermissionGuardProps) {
  const permissions = useRolePermissions()
  const hasPermission = permissions[permission]

  if (!hasPermission) return fallback
  return <>{children}</>
}
